import { Chart, ChartOptions, Plugin } from 'chart.js';

/**
 * Chart colours. Canvas needs concrete colours, so these are the sRGB equivalents of the oklch
 * tokens in styles.scss (shared with the AGW Developer Portal).
 */
export const CHART = {
  primary: '#b2010a',
  foreground: '#020618',
  mutedForeground: '#62748e',
  border: '#e2e8f0',
  ring: '#90a1b9',
  font: "'DM Sans', 'Cerebri Sans', sans-serif",
};

/** One fixed colour per order status, used by every chart, KPI dot and legend. */
export const STATUS_COLORS: Record<string, string> = {
  Booked: '#104e64', // chart-3
  Issued: '#009689', // chart-2
  Voided: '#f54900', // chart-1
  Expired: '#ffb900', // chart-4
  Cancelled: '#90a1b9', // ring
};

/** Shared axis / tooltip styling, matching the Developer Portal's recharts setup. */
export function baseLineOptions(): ChartOptions<'line'> {
  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    elements: {
      line: { tension: 0.35, borderWidth: 2 },
      point: { radius: 0, hoverRadius: 4, hoverBorderWidth: 2, hoverBackgroundColor: '#fff' },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#fff',
        titleColor: CHART.foreground,
        bodyColor: CHART.foreground,
        borderColor: CHART.border,
        borderWidth: 1,
        cornerRadius: 6,
        padding: 10,
        boxPadding: 4,
        usePointStyle: true,
        titleFont: { family: CHART.font, size: 12, weight: 600 },
        bodyFont: { family: CHART.font, size: 12 },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
        ticks: {
          color: CHART.mutedForeground,
          font: { family: CHART.font, size: 11 },
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 7,
        },
      },
      y: {
        beginAtZero: true,
        border: { display: false, dash: [3, 3] },
        grid: { color: CHART.border },
        ticks: {
          color: CHART.mutedForeground,
          font: { family: CHART.font, size: 11 },
          precision: 0,
        },
      },
    },
  };
}

/** Dashed vertical guide under the hovered x position. */
export const hoverLinePlugin: Plugin<'line'> = {
  id: 'hoverLine',
  afterDatasetsDraw(chart: Chart) {
    const active = chart.tooltip?.getActiveElements?.();
    if (!active?.length) return;
    const x = active[0].element.x;
    const { top, bottom } = chart.chartArea;
    const ctx = chart.ctx;
    ctx.save();
    ctx.beginPath();
    ctx.setLineDash([4, 3]);
    ctx.moveTo(x, top);
    ctx.lineTo(x, bottom);
    ctx.lineWidth = 1;
    ctx.strokeStyle = CHART.ring;
    ctx.stroke();
    ctx.restore();
  },
};
